import { useTrans } from '@/shared/hooks/useTranslation';
import { cn } from '@/shared/utils';
import type {
  BreadcrumbSwitcherConfig,
  BreadcrumbSwitcherOption,
} from '@/viewmodel/shared/breadcrumbs';
import type React from 'react';

interface BreadcrumbSwitcherOptionsProps {
  readonly config: BreadcrumbSwitcherConfig;
  readonly options: BreadcrumbSwitcherOption[];
  readonly query: string;
  readonly onSelect: (value: string) => void;
}

export function BreadcrumbSwitcherOptions({
  config,
  options,
  query,
  onSelect,
}: BreadcrumbSwitcherOptionsProps): React.JSX.Element {
  const { t } = useTrans();

  return (
    // biome-ignore lint/a11y/useFocusableInteractive: keyboard navigation handled by individual option buttons
    // biome-ignore lint/a11y/useSemanticElements: custom select with search
    <div className="max-h-52 overflow-y-auto p-1" role="listbox" aria-busy={config.isLoading}>
      {options.length === 0 && !config.isLoading ? (
        <p className="px-3 py-3 text-center text-xs text-muted-foreground">
          {t('common.noResultsFor', { query })}
        </p>
      ) : (
        options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            // biome-ignore lint/a11y/useSemanticElements: button with role="option" inside a listbox for custom select UX
            role="option"
            aria-selected={config.currentValue === opt.value}
            onClick={() => onSelect(opt.value)}
            className={cn(
              'flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 sm:py-1.5 text-xs sm:text-sm transition-colors',
              'focus:outline-none',
              config.currentValue === opt.value
                ? 'bg-primary/5 text-primary'
                : 'text-foreground hover:bg-surface-secondary',
            )}
          >
            <span className="flex-1 text-left truncate">{opt.label}</span>
          </button>
        ))
      )}
      {config.hasMore && config.onLoadMore && (
        <button
          type="button"
          onClick={config.onLoadMore}
          disabled={config.isLoading}
          className="w-full cursor-pointer rounded-md px-2.5 py-2 text-xs text-primary hover:bg-surface-secondary disabled:cursor-wait disabled:opacity-60"
        >
          {t('common.loadMore')}
        </button>
      )}
    </div>
  );
}
